import React, { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import {
  MdOutlineBusiness,
  MdOutlineEmail,
  MdOutlinePhone,
  MdOutlineCalendarToday,
  MdOutlineBadge,
  MdOutlineLanguage,
  MdVerified,
  MdOutlineEdit,
  MdClose,
} from "react-icons/md";
import { FaInstagram, FaTiktok, FaTwitter, FaFacebook } from "react-icons/fa";
import { ProfileContext } from "context/ProfileContext";
import {
  _organizationDetails,
  _updateOrganization,
  _updateUserProfile,
  _userProfile,
} from "Services/api";
import SectionCard from "components/card/SectionCard";
import StatusBadge from "components/badge/StatusBadge";
import AvatarInitials from "components/badge/AvatarInitials";
import InputField from "components/fields/InputField";
import TextField from "components/fields/TextField";
import {
  OrganizationFormFieldsName,
  organizationFormDefaultValues,
  organizationFormSchema,
  ProfileSettingsFormFieldsName,
  profileSettingsFormDefaultValues,
  profileSettingsFormSchema,
} from "constants/react-hook-form";

const getPayloadData = (response) =>
  response?.data?.data || response?.data?.organization || response?.data || response || {};

const getOrganizationId = (organization) =>
  organization?.id || organization?.organization_id || organization?.org_id || organization?.uuid;

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-xl bg-gray-50/70 p-4 dark:bg-white/5">
    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-lg text-brand-500 shadow-sm dark:bg-navy-700 dark:text-white">
      {icon}
    </span>
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">
        {value || "N/A"}
      </p>
    </div>
  </div>
);

const StatCard = ({ label, value }) => (
  <div className="flex flex-col items-center justify-center rounded-2xl bg-white p-4 text-center shadow-lg shadow-gray-200/60 dark:bg-navy-800 dark:shadow-none">
    <p className="text-2xl font-bold text-navy-700 dark:text-white">{value}</p>
    <p className="mt-1 text-xs font-medium text-gray-500">{label}</p>
  </div>
);

const EditToggleButton = ({ isEditing, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
      isEditing
        ? "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200"
        : "bg-brand-50 text-brand-500 hover:bg-brand-100 dark:bg-navy-700 dark:text-white"
    }`}
  >
    {isEditing ? (
      <>
        <MdClose /> Cancel
      </>
    ) : (
      <>
        <MdOutlineEdit /> Edit
      </>
    )}
  </button>
);

const SaveButton = ({ loading, children = "Save Changes" }) => (
  <button
    type="submit"
    disabled={loading}
    className="flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
  >
    {loading ? "Saving..." : children}
  </button>
);

const getBotCoverStyle = (bot) => {
  if (bot?.logo) {
    return {
      backgroundImage: `url(${bot.logo})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }
  if (bot?.bot_color_code) {
    return { backgroundColor: bot.bot_color_code };
  }
  return undefined;
};

const ProfileOverview = () => {
  const profileData = useContext(ProfileContext) || {};
  const accessToken = sessionStorage.getItem("VendorToken");
  const isAdmin = sessionStorage.getItem("isAdmin") === "true";
  const [organization, setOrganization] = useState(null);
  const [organizationId, setOrganizationId] = useState("");
  const [loadingOrganization, setLoadingOrganization] = useState(false);
  const [profileMeta, setProfileMeta] = useState({ email: "", phone_number: "" });
  const [savedProfileValues, setSavedProfileValues] = useState(profileSettingsFormDefaultValues);
  const [savedOrganizationValues, setSavedOrganizationValues] = useState(
    organizationFormDefaultValues
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const botData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("botData") || "null");
    } catch (error) {
      return null;
    }
  }, []);
  const coverStyle = getBotCoverStyle(botData);

  const profileForm = useForm({
    resolver: zodResolver(profileSettingsFormSchema),
    defaultValues: profileSettingsFormDefaultValues,
  });

  const organizationForm = useForm({
    resolver: zodResolver(organizationFormSchema),
    defaultValues: organizationFormDefaultValues,
  });

  useEffect(() => {
    const loadOrganization = async () => {
      if (isAdmin || !accessToken) return;

      setLoadingOrganization(true);
      try {
        const response = await _organizationDetails(accessToken);
        const org = getPayloadData(response);
        setOrganization(org);
        setOrganizationId(getOrganizationId(org));
        const organizationValues = {
          [OrganizationFormFieldsName.NAME]: org.name || "",
          [OrganizationFormFieldsName.WEBSITE]: org.website || "",
          [OrganizationFormFieldsName.DESCRIPTION]: org.description || "",
          [OrganizationFormFieldsName.CONTACT_PHONE]: org.contact_phone || "",
          [OrganizationFormFieldsName.INSTAGRAM]: org.instagram || "",
          [OrganizationFormFieldsName.TIKTOK]: org.tiktok || "",
          [OrganizationFormFieldsName.TWITTER]: org.twitter || "",
          [OrganizationFormFieldsName.FACEBOOK]: org.facebook || "",
        };

        setSavedOrganizationValues(organizationValues);
        organizationForm.reset(organizationValues);
      } catch (error) {
        toast.error(error.response?.data?.message || "Could not load organization details.");
      } finally {
        setLoadingOrganization(false);
      }
    };

    loadOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isAdmin]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!accessToken) return;

      try {
        const response = await _userProfile(accessToken);
        const profile = getPayloadData(response);

        setProfileMeta({
          email: profile.email || "",
          phone_number: profile.phone_number || "",
        });
        const profileValues = {
          [ProfileSettingsFormFieldsName.FIRST_NAME]: profile.first_name || "",
          [ProfileSettingsFormFieldsName.LAST_NAME]: profile.last_name || "",
          [ProfileSettingsFormFieldsName.USERNAME]: profile.username || "",
        };

        setSavedProfileValues(profileValues);
        profileForm.reset(profileValues);
      } catch (error) {
        toast.error(error.response?.data?.message || "Could not load profile details.");
      }
    };

    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleSaveAll = async (profileValues) => {
    setIsSaving(true);
    try {
      const hasProfileChanges =
        profileValues[ProfileSettingsFormFieldsName.FIRST_NAME] !==
          savedProfileValues[ProfileSettingsFormFieldsName.FIRST_NAME] ||
        profileValues[ProfileSettingsFormFieldsName.LAST_NAME] !==
          savedProfileValues[ProfileSettingsFormFieldsName.LAST_NAME] ||
        (profileValues[ProfileSettingsFormFieldsName.USERNAME] || "") !==
          (savedProfileValues[ProfileSettingsFormFieldsName.USERNAME] || "");

      if (hasProfileChanges) {
        const profileBody = {
          first_name: profileValues[ProfileSettingsFormFieldsName.FIRST_NAME],
          last_name: profileValues[ProfileSettingsFormFieldsName.LAST_NAME],
          username: profileValues[ProfileSettingsFormFieldsName.USERNAME] || "",
        };

        await _updateUserProfile({ body: profileBody, accessToken });
        setSavedProfileValues(profileValues);
      }

      let hasOrganizationChanges = false;

      if (!isAdmin) {
        const organizationValid = await organizationForm.trigger();
        if (!organizationValid) {
          setIsSaving(false);
          return;
        }
        if (!organizationId) {
          toast.error("Organization details are not available yet.");
          setIsSaving(false);
          return;
        }

        const organizationValues = organizationForm.getValues();
        const orgFieldsToCheck = [
          OrganizationFormFieldsName.NAME,
          OrganizationFormFieldsName.WEBSITE,
          OrganizationFormFieldsName.DESCRIPTION,
          OrganizationFormFieldsName.CONTACT_PHONE,
          OrganizationFormFieldsName.INSTAGRAM,
          OrganizationFormFieldsName.TIKTOK,
          OrganizationFormFieldsName.TWITTER,
          OrganizationFormFieldsName.FACEBOOK,
        ];
        hasOrganizationChanges = orgFieldsToCheck.some(
          (field) => (organizationValues[field] || "") !== (savedOrganizationValues[field] || "")
        );

        if (hasOrganizationChanges) {
          const organizationBody = {
            name: organizationValues[OrganizationFormFieldsName.NAME],
            website: organizationValues[OrganizationFormFieldsName.WEBSITE],
            description: organizationValues[OrganizationFormFieldsName.DESCRIPTION],
            contact_phone: organizationValues[OrganizationFormFieldsName.CONTACT_PHONE] || "",
            instagram: organizationValues[OrganizationFormFieldsName.INSTAGRAM] || "",
            tiktok: organizationValues[OrganizationFormFieldsName.TIKTOK] || "",
            twitter: organizationValues[OrganizationFormFieldsName.TWITTER] || "",
            facebook: organizationValues[OrganizationFormFieldsName.FACEBOOK] || "",
          };

          const organizationResponse = await _updateOrganization({
            organizationId,
            body: organizationBody,
            accessToken,
          });
          setSavedOrganizationValues(organizationValues);
          setOrganization((current) => ({ ...current, ...organizationBody }));
          toast.success(organizationResponse.message || "Organization updated successfully.");
        }
      }

      if (!hasProfileChanges && !hasOrganizationChanges) {
        toast.info("No changes to save.");
      } else if (hasProfileChanges && !hasOrganizationChanges) {
        toast.success("Profile updated successfully.");
      }

      setIsEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not save your changes.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    profileForm.reset(savedProfileValues);
    organizationForm.reset(savedOrganizationValues);
    setIsEditing(false);
  };

  const fullName =
    [profileData.first_name, profileData.last_name].filter(Boolean).join(" ") || "Vendor Account";
  const roleLabel = isAdmin ? "Administrator" : "Vendor";

  return (
    <div className="flex w-full flex-col gap-5">
      <SectionCard extra="!p-0 overflow-hidden">
        <div
          className="h-24 w-full bg-gradient-to-r from-brand-500 to-brand-700"
          style={coverStyle}
        />
        <div className="flex flex-col gap-4 px-6 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="-mt-10 flex flex-col items-center gap-3 sm:flex-row sm:items-end">
            <span className="flex h-24 w-24 items-center justify-center rounded-full border-4 border-white bg-white shadow-md dark:border-navy-800">
              <AvatarInitials name={fullName} size="h-full w-full" className="text-2xl" />
            </span>
            <div className="text-center sm:text-left">
              <h4 className="text-xl font-bold text-navy-700 dark:text-white">{fullName}</h4>
              <div className="mt-1 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <StatusBadge tone="info">{roleLabel}</StatusBadge>
                {profileData.is_active !== undefined ? (
                  <StatusBadge tone={profileData.is_active ? "success" : "neutral"}>
                    {profileData.is_active ? "Active" : "Inactive"}
                  </StatusBadge>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard label="Username" value={profileData.username || "N/A"} />
        <StatCard label="Role" value={roleLabel} />
        <StatCard
          label="Member Since"
          value={profileData.created_at ? formatDate(profileData.created_at).split(" ")[2] : "N/A"}
        />
        <StatCard
          label="Organization"
          value={organization?.name || (isAdmin ? "Plist Admin" : "N/A")}
        />
      </div>

      <SectionCard
        title="Personal Information"
        description="Your account details as registered on Plist."
        icon={<MdOutlineBadge />}
        actions={
          <EditToggleButton
            isEditing={isEditing}
            onClick={isEditing ? handleCancel : () => setIsEditing(true)}
          />
        }
      >
        <form onSubmit={profileForm.handleSubmit(handleSaveAll)} noValidate>
          {isEditing ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <InputField
                control={profileForm.control}
                name={ProfileSettingsFormFieldsName.FIRST_NAME}
                id="firstName"
                label="First Name"
                type="text"
              />
              <InputField
                control={profileForm.control}
                name={ProfileSettingsFormFieldsName.LAST_NAME}
                id="lastName"
                label="Last Name"
                type="text"
              />
              <InputField
                control={profileForm.control}
                name={ProfileSettingsFormFieldsName.USERNAME}
                id="username"
                label="Username"
                type="text"
              />
              <InputField id="email" label="Email" type="text" value={profileMeta.email} disabled />
              <InputField
                id="phoneNumber"
                label="Phone Number"
                type="text"
                value={profileMeta.phone_number}
                disabled
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <InfoRow icon={<MdOutlineEmail />} label="Email Address" value={profileData.email} />
              <InfoRow
                icon={<MdOutlinePhone />}
                label="Phone Number"
                value={profileData.phone_number}
              />
              <InfoRow icon={<MdOutlineBadge />} label="Username" value={profileData.username} />
              <InfoRow
                icon={<MdOutlineCalendarToday />}
                label="Joined"
                value={formatDate(profileData.created_at)}
              />
            </div>
          )}

          {!isAdmin ? (
            <div className="mt-6 border-t border-gray-100 pt-6 dark:border-white/10">
              <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-navy-700 dark:text-white">
                <MdOutlineBusiness /> Organization
              </h3>
              {loadingOrganization ? (
                <p className="text-sm text-gray-500">Loading organization...</p>
              ) : isEditing ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <InputField
                    control={organizationForm.control}
                    name={OrganizationFormFieldsName.NAME}
                    id="organizationName"
                    label="Organization Name"
                    type="text"
                  />
                  <InputField
                    control={organizationForm.control}
                    name={OrganizationFormFieldsName.WEBSITE}
                    id="organizationWebsite"
                    label="Website"
                    type="text"
                  />
                  <InputField
                    control={organizationForm.control}
                    name={OrganizationFormFieldsName.CONTACT_PHONE}
                    id="organizationContactPhone"
                    label="Contact Phone"
                    type="text"
                  />
                  <div className="sm:col-span-2">
                    <TextField
                      control={organizationForm.control}
                      name={OrganizationFormFieldsName.DESCRIPTION}
                      id="organizationDescription"
                      label="Description"
                      rows={3}
                    />
                  </div>
                  <InputField
                    control={organizationForm.control}
                    name={OrganizationFormFieldsName.INSTAGRAM}
                    id="organizationInstagram"
                    label="Instagram URL"
                    type="text"
                  />
                  <InputField
                    control={organizationForm.control}
                    name={OrganizationFormFieldsName.TIKTOK}
                    id="organizationTiktok"
                    label="TikTok URL"
                    type="text"
                  />
                  <InputField
                    control={organizationForm.control}
                    name={OrganizationFormFieldsName.TWITTER}
                    id="organizationTwitter"
                    label="Twitter / X URL"
                    type="text"
                  />
                  <InputField
                    control={organizationForm.control}
                    name={OrganizationFormFieldsName.FACEBOOK}
                    id="organizationFacebook"
                    label="Facebook URL"
                    type="text"
                  />
                </div>
              ) : organization ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <InfoRow
                    icon={<MdOutlineBusiness />}
                    label="Organization Name"
                    value={organization.name}
                  />
                  <InfoRow
                    icon={<MdOutlineLanguage />}
                    label="Website"
                    value={organization.website}
                  />
                  <InfoRow
                    icon={<MdOutlinePhone />}
                    label="Contact Phone"
                    value={organization.contact_phone}
                  />
                  <div className="sm:col-span-2">
                    <InfoRow
                      icon={<MdVerified />}
                      label="Description"
                      value={organization.description}
                    />
                  </div>
                  {organization.instagram ||
                  organization.tiktok ||
                  organization.twitter ||
                  organization.facebook ? (
                    <>
                      <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-400 sm:col-span-2">
                        Social Media
                      </p>
                      {organization.instagram ? (
                        <InfoRow
                          icon={<FaInstagram />}
                          label="Instagram"
                          value={organization.instagram}
                        />
                      ) : null}
                      {organization.tiktok ? (
                        <InfoRow icon={<FaTiktok />} label="TikTok" value={organization.tiktok} />
                      ) : null}
                      {organization.twitter ? (
                        <InfoRow
                          icon={<FaTwitter />}
                          label="Twitter / X"
                          value={organization.twitter}
                        />
                      ) : null}
                      {organization.facebook ? (
                        <InfoRow
                          icon={<FaFacebook />}
                          label="Facebook"
                          value={organization.facebook}
                        />
                      ) : null}
                    </>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No organization details available yet.</p>
              )}
            </div>
          ) : null}

          {isEditing ? (
            <div className="mt-6 flex justify-end gap-2 border-t border-gray-100 pt-6 dark:border-white/10">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
              >
                Cancel
              </button>
              <SaveButton loading={isSaving} />
            </div>
          ) : null}
        </form>
      </SectionCard>
    </div>
  );
};

export default ProfileOverview;
